import { useMemo } from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appointmentRequestsApi } from '@/api/appointment-requests.api';
import { useBranch } from '@/contexts/BranchContext';
import { useAuth } from '@/contexts/AuthContext';
import { useCan } from '@/hooks/useCan';
import { useRealtimeRefetch } from '@/hooks/useRealtime';
import { useUrlState } from '@/hooks/useUrlState';
import { useDebounce } from '@/hooks/useDebounce';
import type {
  AcceptRequestInput,
  DeclineRequestInput,
  RequestFilters,
  SlotConflict,
} from '@/types/appointment-request.types';

/**
 * Server state for Appointment Requests.
 *
 * The sidebar badge, the page-title badge and the page banner all read
 * `useRequestSummary()` — one query key, one request, one number. They used
 * to be three: the sidebar fetched a clinic-wide count once per mount and
 * bumped it by hand on notifications, the header counted whatever list the
 * page had loaded, and the banner counted rows older than a hardcoded 4 h.
 * All three are now scoped by the same global branch switcher.
 */
export const requestKeys = {
  all: ['appointment-requests'] as const,
  summary: (branchId?: string) =>
    [...requestKeys.all, 'summary', branchId ?? 'all'] as const,
  list: (filters: RequestFilters) =>
    [...requestKeys.all, 'list', filters] as const,
  check: (id: string, input: object) =>
    [...requestKeys.all, 'check', id, input] as const,
};

function useScopedBranchId() {
  const { selectedBranch, isAllBranches } = useBranch();
  return isAllBranches ? undefined : selectedBranch?.id;
}

export function useRequestSummary() {
  const { clinic } = useAuth();
  const { canView } = useCan();
  // The sidebar mounts this for everyone; without reservations:view the API
  // answers 403, so do not ask.
  const enabled = !!clinic?.id && canView('reservations');
  const branchId = useScopedBranchId();
  const query = useQuery({
    queryKey: requestKeys.summary(branchId),
    queryFn: () => appointmentRequestsApi.summary(branchId),
    enabled,
    staleTime: 30_000,
    // Expiry is a function of the clock, not only of writes: a request
    // becomes Expired with nobody touching it.
    refetchInterval: 60_000,
  });
  // Another desk (or the public booking app) changed a reservation.
  useRealtimeRefetch(['reservation'], () => query.refetch(), { enabled });
  return query;
}

export function useRequestList(filters: RequestFilters) {
  const { clinic } = useAuth();
  const branchId = useScopedBranchId();
  const scoped = useMemo(() => ({ ...filters, branchId }), [filters, branchId]);
  const query = useQuery({
    queryKey: requestKeys.list(scoped),
    queryFn: () => appointmentRequestsApi.list(scoped),
    enabled: !!clinic?.id,
    placeholderData: (previous) => previous,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  useRealtimeRefetch(['reservation'], () => query.refetch(), {
    enabled: !!clinic?.id,
  });
  return query;
}

/** Accept preview: re-runs as the doctor / room / time choice changes. */
export function useAcceptCheck(
  id: string | undefined,
  input: { doctorId?: string; roomId?: string; scheduledAt?: string },
) {
  return useQuery({
    queryKey: requestKeys.check(id ?? '', input),
    queryFn: () => appointmentRequestsApi.check(id!, input),
    enabled: !!id,
    staleTime: 0,
  });
}

export function useRequestMutations() {
  const qc = useQueryClient();
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: requestKeys.all }),
      qc.invalidateQueries({ queryKey: ['reservations'] }),
    ]);

  return {
    accept: useMutation({
      mutationFn: ({ id, input }: { id: string; input: AcceptRequestInput }) =>
        appointmentRequestsApi.accept(id, input),
      onSettled: invalidate,
    }),
    decline: useMutation({
      mutationFn: ({ id, input }: { id: string; input: DeclineRequestInput }) =>
        appointmentRequestsApi.decline(id, input),
      onSettled: invalidate,
    }),
    bulkAccept: useMutation({
      mutationFn: appointmentRequestsApi.bulkAccept,
      onSettled: invalidate,
    }),
    bulkDecline: useMutation({
      mutationFn: appointmentRequestsApi.bulkDecline,
      onSettled: invalidate,
    }),
  };
}

/**
 * The coded body the API attaches to a refused decision
 * (`response.data.errors`, per the global exception filter).
 */
export function requestErrorOf(err: unknown): {
  code?: string;
  conflicts?: SlotConflict[];
  message?: string;
} {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | {
          message?: string;
          errors?: { code?: string; conflicts?: SlotConflict[] };
        }
      | undefined;
    return {
      code: data?.errors?.code,
      conflicts: data?.errors?.conflicts,
      message: data?.message,
    };
  }
  const e = err as {
    code?: string;
    errors?: { conflicts?: SlotConflict[] };
    message?: string;
  };
  return {
    code: e?.code,
    conflicts: e?.errors?.conflicts,
    message: e?.message,
  };
}

/* ── URL state ─────────────────────────────────────────────────────── */

const KEYS = ['status', 'q', 'doctor', 'from', 'to'] as const;

/**
 * Everything the user can change about the view lives in the URL, so "the
 * expired ones for Dr. Sara" is a link, survives a refresh and comes back on
 * Back. Branch is not here: it follows the global branch switcher, like every
 * other list, and so do the counts.
 *
 * `status` defaults to `open` (pending + expired) — the work that needs a
 * person. Search is debounced before it becomes a request.
 */
export function useRequestBoardState() {
  const { state, setState, clear } = useUrlState<(typeof KEYS)[number]>(KEYS, {
    defaults: { status: 'open' },
  });
  const search = useDebounce(state.q ?? '', 300);

  const filters: RequestFilters = useMemo(
    () => ({
      status:
        state.status === 'open' || !state.status
          ? 'pending,expired'
          : state.status,
      doctorId: state.doctor || undefined,
      from: state.from || undefined,
      to: state.to || undefined,
      search: search.trim() || undefined,
    }),
    [state.status, state.doctor, state.from, state.to, search],
  );

  const hasActiveFilters =
    (state.status ?? 'open') !== 'open' ||
    !!state.q ||
    !!state.doctor ||
    !!state.from ||
    !!state.to;

  return { state, set: setState, clear, filters, hasActiveFilters };
}
